import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  schema?: object;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogImage,
  schema,
}: SEOProps) => {
  const location = useLocation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://eminesevimkeles.com';
  const fullUrl = canonical || `${siteUrl}${location.pathname}`;
  const defaultImage = `${siteUrl}/og-image.jpg`;

  useEffect(() => {
    // Update title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', description);
    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    // Update canonical
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', fullUrl);

    // Update last-modified
    const today = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', today);

    // Open Graph tags
    updateMetaTag('og:type', 'website', true);
    updateMetaTag('og:url', fullUrl, true);
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:image', ogImage || defaultImage, true);

    // Twitter tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', ogImage || defaultImage);

    // Schema.org JSON-LD
    if (schema) {
      let scriptTag = document.querySelector('script[type="application/ld+json"]');
      
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.setAttribute('type', 'application/ld+json');
        document.head.appendChild(scriptTag);
      }
      
      scriptTag.textContent = JSON.stringify(schema);
    }

    // Cleanup function
    return () => {
      // Remove schema when component unmounts
      const schemaScript = document.querySelector('script[type="application/ld+json"]');
      if (schemaScript) {
        schemaScript.remove();
      }
    };
  }, [title, description, keywords, fullUrl, ogImage, schema, defaultImage]);
};

// Schema.org generators
export const generatePersonSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://eminesevimkeles.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Emine Sevim Keleş',
    url: siteUrl,
    image: `${siteUrl}/og-image.jpg`,
    jobTitle: 'Yazar, Eğitmen & Konuşmacı',
    description: 'Yüzlerce çevrimiçi ve yüz yüze kurs, atölye ve seminer vermiş köklü bir yazar ve deneyimli bir eğitmen.',
    knowsAbout: ['Kuantum Eğitimi', 'Bilinçli Yaşam', 'Kişisel Gelişim', 'Farkındalık'],
    sameAs: [
      'https://www.instagram.com/eminesevimkeles',
      'https://www.facebook.com/eminesevimkeles',
      'https://twitter.com/eminesevimkeles'
    ]
  };
};

export const generateWebSiteSchema = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://eminesevimkeles.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Emine Sevim Keleş',
    url: siteUrl,
    description: 'Yazar, Eğitmen & İlham Verici Konuşmacı - Bilgi, farkındalık ve bilinçli yaşam yoluyla hayatları dönüştürüyorum.',
    publisher: {
      '@type': 'Person',
      name: 'Emine Sevim Keleş'
    }
  };
};

export const generateBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://eminesevimkeles.com';
  
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${siteUrl}${item.url}`
    }))
  };
};